// Scenario 1: Booked - Exact CMS Match
// Shows lodging insights, area card, small map, and lodging profile
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, A11y } from 'swiper/modules';
import { islandScopedDocumentFilter, sanityFetch } from '../../lib/sanity';
import HomebaseActionPanel from './HomebaseActionPanel';
import {
  getHomebaseAreaSlug,
  getHomebaseIslandKey,
  isHomebaseItineraryReady,
  MULTI_ISLAND_PLANNER_COMING_SOON_PATH,
} from '../../utils/homebaseGeography';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const BookedExactScenario = ({ data, interviewId, planningState = {} }) => {
  const navigate = useNavigate();
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [areaData, setAreaData] = useState(null);
  const [lodgingData, setLodgingData] = useState(null);
  const googleEmbedKey = process.env.REACT_APP_GOOGLE_MAPS_EMBED_KEY;
  const VISIBLE_FEATURES = 4;

  // Extract data before early return
  const islandKey = getHomebaseIslandKey(data);
  const itineraryReady = isHomebaseItineraryReady(islandKey);
  const area = data?.area;
  const region = data?.region;
  const lodgingSlug = data?.lodgingSlug;
  const areaSlug = getHomebaseAreaSlug(area, islandKey, data?.areaSlug);
  
  // Fetch real area data from Sanity
  useEffect(() => {
    const fetchAreaData = async () => {
      if (!area) return;
      
      try {
        const query = `*[_type == "area" && ${islandScopedDocumentFilter()} && slug.current == $slug][0]{
          _id,
          title,
          oneLiner,
          heroImage{asset->{url}, alt},
          slug
        }`;
        
        const result = await sanityFetch(query, { slug: areaSlug });
        
        if (result) {
          setAreaData(result);
        } else {
          // Fall back to mock data
          setAreaData({
            title: area,
            heroImage: { asset: { url: '/api/placeholder/400/200' }, alt: `${area} landscape` },
            oneLiner: `Beautiful ${area} offers stunning scenery and local charm in ${region}.`
          });
        }
      } catch (error) {
        console.error('[BookedExactScenario] Error fetching area data:', error);
        // Fall back to mock data
        setAreaData({
          title: area,
          heroImage: { asset: { url: '/api/placeholder/400/200' }, alt: `${area} landscape` },
          oneLiner: `Beautiful ${area} offers stunning scenery and local charm in ${region}.`
        });
      }
    };

    fetchAreaData();
  }, [area, region, areaSlug]);

  // Fetch real lodging data from Sanity
  useEffect(() => {
    const fetchLodgingData = async () => {
      if (!lodgingSlug) return;
      
      try {
        const query = `*[_type == "lodging" && ${islandScopedDocumentFilter()} && slug.current == $slug][0]{
          _id,
          name,
          shortDescription,
          area,
          address,
          priceRange,
          features,
          location,
          phoneNumber,
          website,
          placeID,
          slug,
          images[]{asset->{url}, alt}
        }`;
        
        const result = await sanityFetch(query, { slug: lodgingSlug });
        
        if (result) {
          setLodgingData(result);
        } else {
          // Fall back to basic data
          setLodgingData({
            name: data.lodgingName,
            shortDescription: `A wonderful property in the heart of ${area}`,
            area,
            address: `${area}, ${region}`,
            priceRange: '$$',
            features: ['WiFi', 'Kitchen', 'Parking', 'Ocean View', 'A/C', 'Pool'],
            images: [
              { asset: { url: '/api/placeholder/500/300' }, alt: `${data.lodgingName} exterior` }
            ],
            placeID: null,
            location: null
          });
        }
      } catch (error) {
        console.error('[BookedExactScenario] Error fetching lodging data:', error);
        // Fall back to basic data
        setLodgingData({
          name: data.lodgingName,
          shortDescription: `A wonderful property in the heart of ${area}`,
          area,
          address: `${area}, ${region}`,
          priceRange: '$$',
          features: ['WiFi', 'Kitchen', 'Parking', 'Ocean View', 'A/C', 'Pool'],
          images: [
            { asset: { url: '/api/placeholder/500/300' }, alt: `${data.lodgingName} exterior` }
          ],
          placeID: null,
          location: null
        });
      }
    };

    fetchLodgingData();
  }, [lodgingSlug, area, region, data.lodgingName]);

  // Early return check after hooks
  if (!data || !data.summary) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-white/70">No homebase data available for this scenario.</p>
      </div>
    );
  }

  const { lodgingName, summary } = data;
  const { lodgingInsights, proximityAdvantages, locationStrategies } = summary;

  const handleGenerateItinerary = () => {
    if (!itineraryReady) {
      navigate(MULTI_ISLAND_PLANNER_COMING_SOON_PATH);
      return;
    }
    // Navigate immediately to preview page to show factoid rotator
    navigate(`/itinerary/${interviewId}`);
  };

  const handleEditInterview = () => {
    navigate(`/interview/review/${interviewId}`);
  };

  const handleAreaClick = () => {
    window.open(`/areas/${areaSlug}`, '_blank');
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 pb-8">
        
        {/* Primary CTAs */}
        <div className="flex flex-col md:flex-row md:justify-between gap-4 mb-4">
          <button 
            onClick={handleEditInterview}
            className="btn-secondary w-full md:w-72 px-8 py-4 text-lg"
          >
            Edit Interview
          </button>
          <div className="w-full md:w-72">
            <HomebaseActionPanel
              planningState={planningState}
              onGenerate={handleGenerateItinerary}
              itineraryReady={itineraryReady}
              onUnavailable={() => navigate(MULTI_ISLAND_PLANNER_COMING_SOON_PATH)}
            />
          </div>
        </div>

        {/* LLM Insights */}
        <div className="glass-card rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4 text-white">
            Why {lodgingName} is Perfect for Your Trip
          </h2>
          
          <div className="mb-4">
            <p className="text-white/90 leading-relaxed">
              {lodgingInsights}
            </p>
          </div>

          {proximityAdvantages && proximityAdvantages.length > 0 && (
            <div className="mb-4">
              <h3 className="font-semibold text-white mb-2">Location Advantages:</h3>
              <ul className="space-y-2">
                {proximityAdvantages.map((advantage, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-400 mr-2">•</span>
                    <span className="text-white/90">{advantage}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {locationStrategies && locationStrategies.length > 0 && (
            <div>
              <h3 className="font-semibold text-white mb-2">Strategic Insights:</h3>
              <ul className="space-y-2">
                {locationStrategies.map((strategy, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-400 mr-2">•</span>
                    <span className="text-white/90">{strategy}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Area Card and Map - Side by side on desktop, stacked on mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          
          {/* Area Card - Full width, following RegionDetail pattern */}
          {areaData ? (
            <div 
              className="border border-gray-600 rounded-lg overflow-hidden group hover:text-green-300 transition-colors cursor-pointer"
              onClick={handleAreaClick}
            >
              {/* Area Name Header */}
              <div className="p-4 bg-white/5">
                <h3 className="text-xl font-semibold">{areaData.title}</h3>
              </div>
              
              {/* Hero Image */}
              <div className="w-full h-48">
                <img
                  src={areaData.heroImage?.asset?.url}
                  alt={areaData.heroImage?.alt || `${areaData.title} landscape`}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Description */}
              <div className="p-4">
                <p className="text-white group-hover:text-green-300 transition-colors">
                  {areaData.oneLiner}
                </p>
              </div>
            </div>
          ) : (
            <div className="border border-gray-600 rounded-lg overflow-hidden flex items-center justify-center h-48">
              <p className="text-white/70">Loading area information...</p>
            </div>
          )}

          {/* Property Map - Same height as area card */}
          <div className="border border-gray-600 rounded-lg overflow-hidden">
            {lodgingData?.placeID ? (
              <>
                <iframe
                  title="Google Map"
                  width="100%"
                  height="100%"
                  className="w-full h-full min-h-[300px]"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  src={`https://www.google.com/maps/embed/v1/place?key=${googleEmbedKey}&q=place_id:${lodgingData.placeID}`}
                />
                <div className="text-xs p-2 text-center text-white/50 bg-white/5">
                  Map data © Google
                </div>
              </>
            ) : lodgingData?.location?.lat && lodgingData?.location?.lng ? (
              <>
                <iframe
                  title="Google Map"
                  width="100%"
                  height="100%"
                  className="w-full h-full min-h-[300px]"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  src={`https://www.google.com/maps?q=${lodgingData.location.lat},${lodgingData.location.lng}&z=16&output=embed`}
                />
                <div className="text-xs p-2 text-center text-white/50 bg-white/5">
                  Map data © Google
                </div>
              </>
            ) : (
              <div className="bg-white/5 h-full min-h-[300px] flex items-center justify-center">
                <div className="text-center p-6">
                  <p className="text-white/90 mb-2">Interactive Map</p>
                  <p className="text-sm text-white/70">
                    Showing {lodgingName} in {area}
                  </p>
                  <p className="text-xs text-white/50 mt-2">
                    [Map will display when location data is available]
                  </p>
                </div>
              </div>
            )}
          </div>
          
        </div>

        {/* Lodging Profile - Following LodgingProfile.jsx pattern */}
        {lodgingData ? (
          <div className="mb-8">
            {/* Property Name and Description */}
            <h1 className="text-4xl font-bold text-white">{lodgingData.name || lodgingName}</h1>
            <p className="mt-2 italic text-white/90">{lodgingData.shortDescription}</p>
          
            {/* Image Carousel */}
            {lodgingData.images?.length > 0 && (
            <div className="mt-6 relative">
              <Swiper
                modules={[Navigation, Pagination, Autoplay, A11y]}
                slidesPerView={1}
                loop
                autoplay={{ delay: 5000, disableOnInteraction: false }}
                speed={600}
                navigation
                pagination={{ clickable: true }}
                className="w-full max-h-[500px] rounded-lg shadow-md"
              >
                {lodgingData.images.map((img, idx) => (
                  <SwiperSlide key={idx}>
                    <img
                      src={img.asset?.url}
                      alt={img.alt || `${lodgingData.name || lodgingName} image ${idx + 1}`}
                      className="w-full h-[500px] object-cover pointer-events-none"
                      loading="lazy"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
              <div className="text-xs text-center text-white/50 mt-1">
                Images from Google
              </div>
            </div>
          )}
          
          {/* Metadata Box */}
          <div className="mt-8">
            <div className="bg-white/5 border border-white/10 rounded-lg p-6 shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="mb-2 text-white">
                    <strong>Area:</strong> <span className="text-white/90">{lodgingData.area || area}</span>
                  </div>
                  <div className="mb-2 text-white">
                    <strong>Price:</strong> <span className="text-white/90">{lodgingData.priceRange}</span>
                  </div>
                  {lodgingData.address && (
                    <div className="mb-2 text-white">
                      <strong>Address:</strong> <span className="text-white/90">{lodgingData.address}</span>
                    </div>
                  )}
                  {lodgingData.phoneNumber && (
                    <div className="mb-2 text-white">
                      <strong>Phone:</strong> <span className="text-white/90">{lodgingData.phoneNumber}</span>
                    </div>
                  )}
                  {lodgingData.website && (
                    <div className="mb-2 text-white">
                      <strong>Website:</strong>{' '}
                      <a
                        href={lodgingData.website}
                        className="underline hover:text-gray-300 text-white/90"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {lodgingData.website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                </div>

                <div>
                  <strong className="text-white">Features:</strong>
                  <ul className="list-disc ml-6 mb-2 text-sm text-white/90">
                    {(showAllFeatures
                      ? lodgingData.features
                      : lodgingData.features?.slice(0, VISIBLE_FEATURES) || []
                    ).map((f, i) => (
                      <li key={i} className="mb-1">{f}</li>
                    ))}
                  </ul>
                  {lodgingData.features && lodgingData.features.length > VISIBLE_FEATURES && (
                    <button
                      className="text-sm underline hover:text-gray-300 mt-1 text-white/90"
                      onClick={() => setShowAllFeatures(x => !x)}
                    >
                      {showAllFeatures
                        ? 'Show less'
                        : `+${lodgingData.features.length - VISIBLE_FEATURES} more`}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          </div>
        ) : (
          <div className="mb-8 flex items-center justify-center h-48">
            <p className="text-white/70">Loading lodging information...</p>
          </div>
        )}

        {/* Bottom CTAs */}
        <div className="flex flex-col md:flex-row md:justify-between gap-4">
          <button 
            onClick={handleEditInterview}
            className="btn-secondary w-full md:w-72 px-8 py-4 text-lg"
          >
            Edit Interview
          </button>
          <div className="w-full md:w-72">
            <HomebaseActionPanel
              planningState={planningState}
              onGenerate={handleGenerateItinerary}
              itineraryReady={itineraryReady}
              onUnavailable={() => navigate(MULTI_ISLAND_PLANNER_COMING_SOON_PATH)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookedExactScenario;
